/* ==========================================================================
   cust-drop-gate.js — Viori limited drop gate (v3, four stages)

   Companion to sections/cust-drop-gate.liquid. Everything that has to run in
   the browser lives here, for the same reason cust-hp2-capture.js exists:
   an inline <script> in the section binds too late, clicks fall through to
   Shopify's native form and trip the bot challenge.

   Jobs, in order:
     1. Relocate the gate to sit directly above the cart form.
     2. Disable the add-to-cart controls in every stage except public.
     3. Tick the countdown to the next stage switch.
     4. Post email captures to Klaviyo (teaser + sold-out stages).

   Config arrives on the root element as data-drop-* attributes. No Liquid
   in this file.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__vioriDropGateBound) return;
  window.__vioriDropGateBound = true;

  var REVISION        = '2025-04-15';
  var CALL_TIMEOUT_MS = 2500;
  var RETRY_DELAY_MS  = 1000;

  /* ---------- small helpers ---------- */

  function ValidationError(message) { this.name = 'ValidationError'; this.message = message; }
  ValidationError.prototype = Object.create(Error.prototype);

  function withTimeout(promise, label) {
    var timer;
    var timeout = new Promise(function (_, reject) {
      timer = setTimeout(function () {
        reject(new Error(label + ' timed out after ' + CALL_TIMEOUT_MS + 'ms'));
      }, CALL_TIMEOUT_MS);
    });
    return Promise.race([promise, timeout]).finally(function () { clearTimeout(timer); });
  }

  function roots() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-drop-root]'));
  }

  function cfgFor(root) {
    var d = root.dataset;
    return {
      stage:     d.dropStage || '',
      sectionId: d.dropRoot || '',
      companyId: d.dropCompanyId || '',
      listId:    d.dropList || '',
      nextAt:    d.dropNextAt || '',      // ISO 8601, or empty
      nextStage: d.dropNextStage || '',
      handle:    d.dropHandle || '',
      title:     d.dropTitle || '',
      isMember:  d.dropMember === 'true',
      badInput:  d.dropBadInput || 'Please check your email address.'
    };
  }

  /* ---------- 1. relocation --------------------------------------------
     The gate is declared as its own section so it stays editable, which means
     Shopify renders it wherever the template's order puts it. It belongs
     against the buy box. Retry briefly: on some templates the product form is
     hydrated by JS after first paint. */

  // Where the gate belongs, best target first. The Core Bar product section
  // builds its cart payload in JS and has no <form action="/cart/add">, so the
  // add-to-cart row is the only reliable anchor there.
  var ANCHORS = [
    '.v6-pdp-sc__atc-row',
    '[data-atc-button]',
    'form[action*="/cart/add"]',
    '.shopify-payment-button',
    '.product-form'
  ];

  function cartForm() {
    for (var i = 0; i < ANCHORS.length; i++) {
      var el = document.querySelector(ANCHORS[i]);
      if (el) return el;
    }
    return null;
  }

  function place(root, attempt) {
    attempt = attempt || 0;
    var f = cartForm();
    if (f && f.parentNode) {
      f.parentNode.insertBefore(root, f);
      root.setAttribute('data-drop-placed', '1');
      return;
    }
    if (attempt < 20) {
      setTimeout(function () { place(root, attempt + 1); }, 150);
      return;
    }
    // No cart form on the page at all (sold out, or a template without one).
    // Reveal it where it already sits rather than leaving it hidden.
    root.setAttribute('data-drop-placed', '1');
  }

  /* ---------- 2. no path to the cart, except in public ------------------
     The section already emits scoped CSS that hides the purchase controls.
     This disables them too, so a keyboard user or a script cannot submit a
     form that is merely invisible. */

  function sealCart() {
    document.querySelectorAll('form[action*="/cart/add"]').forEach(function (f) {
      f.setAttribute('data-drop-sealed', '1');
      f.querySelectorAll('button, input[type=submit]').forEach(function (b) {
        b.disabled = true;
        b.setAttribute('aria-disabled', 'true');
      });
    });
    // Core Bar buy box + anything else that can reach the cart.
    var sels = ['[data-atc-button]', '.v6-pdp-sc__atc', '.shopify-payment-button button',
                '.v6-sticky-buy button', '.v6-sticky-buy a'];
    sels.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (b) {
        b.disabled = true;
        b.setAttribute('aria-disabled', 'true');
        b.setAttribute('data-drop-sealed', '1');
      });
    });
  }

  /* ---------- 2b. member pricing selector (public stage) ----------------
     Everyone can select it and see the member price. Only a signed-in customer
     carrying the member tag can buy at it; everyone else gets a join CTA in
     place of add-to-cart.

     The tile is a CLONE of the theme's own "One-time" label, so it picks up the
     segmented-control styling without this file knowing anything about it. */

  var MONEY_IN_HTML = /(\d[\d,]*\.\d{2})/g;

  // Every money node the member price should rewrite.
  /* Deliberately NOT the purchase-row tiles ([data-purchase-radio]
     [data-tile-price]) — "One-time" must keep showing the regular price or the
     choice between it and "Member" means nothing. */
  var PRICE_NODES = ['[data-total]', '[data-bundle-price]', '[data-headline-price]'];

  function priceNodes() {
    var out = [];
    PRICE_NODES.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) { out.push(el); });
    });
    return out;
  }

  function discount(html, keep) {
    return html.replace(MONEY_IN_HTML, function (whole) {
      var n = parseFloat(whole.replace(/,/g, ''));
      return isNaN(n) ? whole : (n * keep).toFixed(2);
    });
  }

  /* The theme re-renders these nodes on every interaction, and it does so
     AFTER our click handler runs. So this never trusts a one-time snapshot:
     on each pass, anything we did not write ourselves is treated as a fresh
     value from the theme, stashed, and discounted. Idempotent, so the observer
     below can call it as often as it likes. */
  function applyMember(pct) {
    var keep = (100 - pct) / 100;
    priceNodes().forEach(function (el) {
      var cur = el.innerHTML;
      if (el.__dropWrote === cur) return;        // ours, already correct
      el.__dropOrig = cur;                        // theme's latest value
      var next = discount(cur, keep);
      if (next !== cur) { el.innerHTML = next; el.__dropWrote = next; }
      el.setAttribute('data-drop-repriced', '1');
    });
  }

  function clearMember() {
    priceNodes().forEach(function (el) {
      if (el.__dropOrig != null && el.innerHTML === el.__dropWrote) {
        el.innerHTML = el.__dropOrig;
      }
      el.__dropWrote = null;
      el.__dropOrig = null;
      el.removeAttribute('data-drop-repriced');
    });
  }

  // One observer over the whole buy box, so a re-render anywhere is caught.
  function watchPrices(root) {
    if (root.__dropWatching || !('MutationObserver' in window)) return;
    var box = document.querySelector('.v6-pdp-sc__buybox') ||
              document.querySelector('[data-purchase-radio]');
    if (!box) box = document.body;
    root.__dropWatching = true;
    var pending = false;
    new MutationObserver(function () {
      if (root.getAttribute('data-drop-member-mode') !== '1' || pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        applyMember(parseInt(root.getAttribute('data-drop-discount') || '20', 10));
      });
    }).observe(box, { childList: true, characterData: true, subtree: true });
  }

  function memberMode(root) { return root.getAttribute('data-drop-member-mode') === '1'; }

  function setMemberMode(root, on) {
    var cfg = cfgFor(root);
    var pick = root.__dropTile;
    var links = document.querySelector('[data-drop-mlinks]');
    var mcta = document.querySelector('[data-drop-mcta]');
    var atcRow = document.querySelector('.v6-pdp-sc__atc-row') ||
                 (document.querySelector('[data-atc-button]') || {}).parentNode;

    root.setAttribute('data-drop-member-mode', on ? '1' : '0');

    if (pick) pick.classList.toggle('is-selected', on);
    // Selecting Member deselects the theme's own options, and vice versa.
    document.querySelectorAll('[data-purchase-radio] > label').forEach(function (l) {
      if (l === pick) return;
      if (on) {
        l.classList.remove('is-selected');
        var r = l.querySelector('input[type=radio]');
        if (r) r.checked = false;
      }
    });

    if (links) links.hidden = !on;

    var pct = parseInt(root.getAttribute('data-drop-discount') || '20', 10);
    if (on) {
      watchPrices(root);
      applyMember(pct);
      // The theme renders after our click handler; catch up once it has.
      setTimeout(function () { applyMember(pct); }, 60);
      requestAnimationFrame(function () { applyMember(pct); });
      // Non-members see a join button where add-to-cart was.
      if (!cfg.isMember) {
        if (atcRow) atcRow.style.display = 'none';
        if (mcta) mcta.hidden = false;
      }
    } else {
      clearMember();
      if (atcRow) atcRow.style.display = '';
      if (mcta) mcta.hidden = true;
    }
  }

  function buildMemberTile(root) {
    var src = document.querySelector('[data-drop-memberpick]');
    var row = document.querySelector('[data-purchase-radio]');
    if (!src || !row || root.__dropTile) return;

    var model = row.querySelector('label[data-purchase="onetime"]') || row.querySelector('label');
    if (!model) return;

    var tile = model.cloneNode(true);
    tile.setAttribute('data-purchase', 'member');
    tile.setAttribute('data-drop-member-tile', '1');
    tile.classList.remove('is-selected');

    var radio = tile.querySelector('input[type=radio]');
    if (radio) { radio.checked = false; radio.value = 'member'; radio.removeAttribute('name'); }

    var title = tile.querySelector('.p-title');
    if (title) title.textContent = src.getAttribute('data-drop-member-title') || 'Member';

    var price = tile.querySelector('[data-tile-price], .p-tile-price');
    if (price) {
      price.textContent = src.getAttribute('data-drop-member-price') || '';
      // Not a live price node — keep it out of the reprice sweep.
      price.removeAttribute('data-tile-price');
    }

    row.appendChild(tile);
    root.__dropTile = tile;
    root.setAttribute('data-drop-discount', src.getAttribute('data-drop-discount') || '20');

    // Links belong directly under the purchase row.
    var links = document.querySelector('[data-drop-mlinks]');
    if (links && row.parentNode) row.parentNode.insertBefore(links, row.nextSibling);

    // The join CTA belongs where add-to-cart is.
    var mcta = document.querySelector('[data-drop-mcta]');
    var atcRow = document.querySelector('.v6-pdp-sc__atc-row');
    if (mcta && atcRow && atcRow.parentNode) atcRow.parentNode.insertBefore(mcta, atcRow.nextSibling);

    // Any click on one of the theme's own options leaves member mode.
    row.addEventListener('click', function (e) {
      var lbl = e.target.closest && e.target.closest('label');
      if (!lbl) return;
      if (lbl === tile) { setMemberMode(root, true); return; }
      if (memberMode(root)) setMemberMode(root, false);
    }, true);

    watchPrices(root);
  }

  /* ---------- 3. countdown --------------------------------------------- */

  var UNITS = [
    ['day',    86400000],
    ['hour',   3600000],
    ['minute', 60000]
  ];

  function humanGap(ms) {
    if (ms <= 0) return null;
    var parts = [];
    var rest = ms;
    for (var i = 0; i < UNITS.length; i++) {
      var n = Math.floor(rest / UNITS[i][1]);
      rest -= n * UNITS[i][1];
      if (n > 0) parts.push(n + ' ' + UNITS[i][0] + (n === 1 ? '' : 's'));
      if (parts.length === 2) break;
    }
    if (!parts.length) return 'less than a minute';
    return parts.join(' ');
  }

  function startCountdown(root, cfg) {
    var el = root.querySelector('[data-drop-countdown]');
    if (!el || !cfg.nextAt) return;
    var target = Date.parse(cfg.nextAt);
    if (isNaN(target)) return;
    var tpl = el.getAttribute('data-drop-countdown') || '{time}';

    function tick() {
      var gap = humanGap(target - Date.now());
      if (!gap) {
        // The switch moment has passed. The server decides the stage, so a
        // reload is the honest way to show it — but only once.
        if (!el.dataset.dropExpired) {
          el.dataset.dropExpired = '1';
          el.textContent = 'Opening now — refresh the page';
        }
        return;
      }
      el.textContent = tpl.replace('{time}', gap);
      setTimeout(tick, 30000);
    }
    tick();
  }

  /* ---------- 4. Klaviyo capture --------------------------------------- */

  function buildPayload(cfg, email) {
    var props = {
      capture_surface: 'limited_drop_' + cfg.stage,
      capture_stage: cfg.stage,
      capture_product: cfg.title,
      capture_product_handle: cfg.handle,
      capture_was_member: cfg.isMember ? 'yes' : 'no',
      capture_page_path: window.location.pathname
    };
    try {
      if (window.VioriAttribution && typeof window.VioriAttribution.snapshot === 'function') {
        var attribution = window.VioriAttribution.snapshot() || {};
        props = Object.assign({}, attribution, props);
      }
    } catch (ex) {
      console.warn('[drop-gate] attribution snapshot unavailable:', ex);
    }
    var clean = {};
    Object.keys(props).forEach(function (k) {
      var v = props[k];
      if (v === null || v === undefined) return;
      var s = String(v);
      if (s) clean[k] = s;
    });

    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Limited Drop — ' + (cfg.title || 'product page'),
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                properties: clean
              }
            }
          }
        },
        relationships: { list: { data: { type: 'list', id: cfg.listId } } }
      }
    };
  }

  function postToKlaviyo(cfg, email) {
    if (!cfg.companyId || !cfg.listId) {
      return Promise.reject(new Error('Klaviyo company_id or list_id is missing'));
    }
    return withTimeout(fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(cfg.companyId), {
      method: 'POST',
      headers: { 'revision': REVISION, 'content-type': 'application/json' },
      body: JSON.stringify(buildPayload(cfg, email))
    }).then(function (res) {
      if (res.status === 202 || res.status === 200 || res.status === 204) return res;
      if (res.status === 400) {
        return res.text().then(function () { throw new ValidationError(cfg.badInput); });
      }
      return res.text().then(function (t) {
        throw new Error('Klaviyo ' + res.status + ': ' + t.slice(0, 240));
      });
    }), 'Klaviyo subscription POST');
  }

  function postWithRetry(cfg, email) {
    return postToKlaviyo(cfg, email).catch(function (err) {
      if (err instanceof ValidationError) throw err;
      console.warn('[drop-gate] attempt 1 failed — retrying once in ' + RETRY_DELAY_MS + 'ms:', err);
      return new Promise(function (r) { setTimeout(r, RETRY_DELAY_MS); })
        .then(function () { return postToKlaviyo(cfg, email); });
    });
  }

  function looksLikeEmail(v) {
    return typeof v === 'string' && /^[^@\s]+@[^@\s.]+\.[^@\s]+$/.test(v.trim());
  }

  function showError(root, msg) {
    var el = root.querySelector('[data-drop-error]');
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
  }

  function clearError(root) {
    var el = root.querySelector('[data-drop-error]');
    if (!el) return;
    el.textContent = '';
    el.hidden = true;
  }

  function submitCapture(root) {
    var cfg = cfgFor(root);
    var input = root.querySelector('[data-drop-email]');
    var btn = root.querySelector('[data-drop-submit]');
    if (!input || !btn || btn.disabled) return;

    var email = (input.value || '').trim();
    if (!looksLikeEmail(email)) {
      showError(root, cfg.badInput);
      input.focus();
      return;
    }
    clearError(root);

    var label = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Sending…';

    postWithRetry(cfg, email).then(function () {
      var form = root.querySelector('[data-drop-capture]');
      var done = root.querySelector('[data-drop-success]');
      if (form) form.hidden = true;
      if (done) done.hidden = false;
      try { if (window.gtag) window.gtag('event', 'email_capture', { source: 'limited_drop_' + cfg.stage }); } catch (e) {}
    }).catch(function (err) {
      showError(root, err instanceof ValidationError ? err.message : 'Something went wrong — try again.');
    }).finally(function () {
      btn.disabled = false;
      btn.textContent = label;
    });
  }

  /* ---------- wiring ---------------------------------------------------- */

  function onClick(e) {
    var btn = e.target.closest && e.target.closest('[data-drop-submit]');
    if (!btn) return;
    var root = btn.closest('[data-drop-root]');
    if (!root) return;
    e.preventDefault();
    submitCapture(root);
  }

  function onKeydown(e) {
    if (e.key !== 'Enter') return;
    var input = e.target.closest && e.target.closest('[data-drop-email]');
    if (!input) return;
    var root = input.closest('[data-drop-root]');
    if (!root) return;
    e.preventDefault();
    submitCapture(root);
  }

  document.addEventListener('click', onClick, true);
  document.addEventListener('keydown', onKeydown, true);

  function boot() {
    roots().forEach(function (root) {
      var cfg = cfgFor(root);
      place(root, 0);
      if (cfg.stage !== 'public') sealCart();
      if (cfg.stage === 'public') setTimeout(function () { buildMemberTile(root); }, 300);
      startCountdown(root, cfg);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor re-renders the section on every edit.
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('[data-drop-root]')) boot();
  });
})();
